import DeliveryLocation from 'src/models/delivery-location/DeliveryLocation'
import Location from 'src/models/location/Location'
import LocationGroup from 'src/models/location/LocationGroup'
import Model, { ID, Integer, JsonObject, MODEL_LEVEL } from 'src/models/model'
import Archivable from 'src/models/model/concerns/Archivable'
import Librariable from 'src/models/model/concerns/Librariable'
import TipPoolShare from 'src/models/tip/TipPoolShare'

export const SOURCES = {
  TIP: 'tip',
  ORDER_SUBTOTAL: 'order-subtotal',
  ORDER_TOTAL: 'order-total'
}
SOURCES.ALL = _.values(SOURCES)
Object.freeze(SOURCES)

export const SOURCE_CONDITIONS = {
  ORDER_ITEM_TYPES: 'order-item-types'
}
SOURCE_CONDITIONS.ALL = _.values(SOURCE_CONDITIONS)
SOURCE_CONDITIONS[SOURCES.TIP] = []
SOURCE_CONDITIONS[SOURCES.ORDER_SUBTOTAL] = SOURCE_CONDITIONS.ALL
SOURCE_CONDITIONS[SOURCES.ORDER_TOTAL] = SOURCE_CONDITIONS.ALL
Object.freeze(SOURCE_CONDITIONS)

export const SOURCE_CONDITIONS_REQUIREMENTS = {
  ORDER_ITEM_TYPES: 'orderItemTypes',
  EXCLUDE_ORDER_ITEM_TYPES: 'excludeOrderItemTypes'
}
SOURCE_CONDITIONS_REQUIREMENTS.ALL = _.values(SOURCE_CONDITIONS_REQUIREMENTS)
Object.freeze(SOURCE_CONDITIONS_REQUIREMENTS)

export const CONDITIONS = {
  AMOUNT: 'amount',
  DELIVERY_TYPES: 'delivery-types',
  DELIVERY_LOCATIONS: 'delivery-locations',
  LOCATIONS: 'locations',
  LOCATION_GROUPS: 'location-groups',
  ORDER_ITEM_TYPES: 'order-item-types'
}
CONDITIONS.ALL = _.values(CONDITIONS)
Object.freeze(CONDITIONS)

export const CONDITIONS_REQUIREMENTS = {
  // AMOUNT
  MIN_AMOUNT: 'minAmount',
  MAX_AMOUNT: 'maxAmount',
  EXCLUDE_AMOUNT: 'excludeAmount',
  // DELIVERY_TYPES
  DELIVERY_TYPES: 'deliveryTypes',
  EXCLUDE_DELIVERY_TYPES: 'excludeDeliveryTypes',
  // DELIVERY_LOCATIONS
  DELIVERY_LOCATIONS: 'deliveryLocationIds',
  EXCLUDE_DELIVERY_LOCATIONS: 'excludeDeliveryLocations',
  // LOCATIONS
  LOCATIONS: 'locationIds',
  EXCLUDE_LOCATIONS: 'excludeLocations',
  // LOCATION_GROUPS
  LOCATION_GROUPS: 'locationGroupIds',
  EXCLUDE_LOCATION_GROUPS: 'excludeLocationGroups',
  // ORDER_ITEM_TYPES
  ORDER_ITEM_TYPES: 'orderItemTypes',
  EXCLUDE_ORDER_ITEM_TYPES: 'excludeOrderItemTypes'
}
CONDITIONS_REQUIREMENTS.ALL = _.values(CONDITIONS_REQUIREMENTS)
Object.freeze(CONDITIONS_REQUIREMENTS)

export default class TipShareRule extends Model {
  static name = 'TipShareRule'

  static level = MODEL_LEVEL.SITE

  static deletable = true
  static duplicatable = true
  static paginate = true
  static versioned = true

  static employeeRequiredPermissionCategories = [Model.permissionCategories.Tipping]

  static define () {
    this.concerns = [Archivable, Librariable]

    this.fields = {
      id: {
        type: ID
      },
      name: {
        type: String,
        sortable: true
      },
      tipOutPercentageThousandths: {
        type: Integer,
        default: 1000
      },
      tipOutSource: {
        type: String,
        default: SOURCES.TIP
      },
      tipOutSourceConditions: {
        type: JsonObject,
        mergeDefault: true,
        default: () => _(SOURCE_CONDITIONS.ALL).keyBy().mapValues(() => false).value()
      },
      tipOutSourceConditionsRequirements: {
        type: JsonObject,
        mergeDefault: true,
        default: () => ({
          [SOURCE_CONDITIONS_REQUIREMENTS.ORDER_ITEM_TYPES]: [],
          [SOURCE_CONDITIONS_REQUIREMENTS.EXCLUDE_ORDER_ITEM_TYPES]: false
        })
      },
      tipPoolShares: {
        type: [TipPoolShare],
        default: () => ([]),
        allowDestroy: true,
        acceptsNestedAttributes: true
      },
      conditions: {
        type: JsonObject,
        mergeDefault: true,
        default: () => _(CONDITIONS.ALL).keyBy().mapValues(() => false).value()
      },
      conditionsRequirements: {
        type: JsonObject,
        mergeDefault: true,
        default: () => ({
          [CONDITIONS_REQUIREMENTS.MIN_AMOUNT]: 0,
          [CONDITIONS_REQUIREMENTS.MAX_AMOUNT]: null,
          [CONDITIONS_REQUIREMENTS.EXCLUDE_AMOUNT]: false,
          [CONDITIONS_REQUIREMENTS.DELIVERY_TYPES]: [],
          [CONDITIONS_REQUIREMENTS.EXCLUDE_DELIVERY_TYPES]: false,
          [CONDITIONS_REQUIREMENTS.EXCLUDE_DELIVERY_LOCATIONS]: false,
          [CONDITIONS_REQUIREMENTS.EXCLUDE_LOCATIONS]: false,
          [CONDITIONS_REQUIREMENTS.ORDER_ITEM_TYPES]: [],
          [CONDITIONS_REQUIREMENTS.EXCLUDE_ORDER_ITEM_TYPES]: false
        })
      },
      deliveryLocations: {
        type: [DeliveryLocation],
        default: () => ([]),
        reference: true
      },
      locations: {
        type: [Location],
        default: () => ([]),
        reference: true
      },
      locationGroups: {
        type: [LocationGroup],
        default: () => ([]),
        reference: true
      },
      priorityOrder: {
        type: Integer,
        default: 100
      }
    }
  }
}
